import { apiEndpoints } from '@/apiEndpoints';
import { Employee } from '@/types/profile/employeeColumnDef';
import { EmployeeFormData } from '@/types/profile/EmployeeFormData';
import { queryKeys } from '@/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const getMe = async () => {
  const response = await axios.get(apiEndpoints.users.me);
  return response.data;
};

export const useGetMe = () =>
  useQuery({ queryKey: queryKeys.me, queryFn: getMe });

const updateUserData = async (userData: FormData) =>
  axios.put(apiEndpoints.users.base, userData);

export const useUpdateUserData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUserData,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.me }),
  });
};

const updateCompanyData = async (companyId: string, companyData: FormData) =>
  axios.put(apiEndpoints.companies.companyUpdate(companyId), companyData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const useUpdateCompanyData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      companyData,
    }: {
      companyId: string;
      companyData: FormData;
    }) => updateCompanyData(companyId, companyData),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.me }),
  });
};

const createUser = async (userData: FormData) => {
  return axios.post(apiEndpoints.users.base, userData);
};

export const useCreateUser = () => {
  return useMutation({
    mutationFn: (userData: FormData) => createUser(userData),
  });
};

type NetworkResponse = {
  id: string;
  name: string;
};

const getNetworks = async () => {
  const response = await axios.get<NetworkResponse[]>(
    apiEndpoints.users.networks,
  );
  return response.data;
};

export const useGetNetworks = <TData = NetworkResponse[]>(
  select?: (data: NetworkResponse[]) => TData,
) => {
  return useQuery({
    queryKey: queryKeys.networks,
    queryFn: () => getNetworks(),
    select,
  });
};

type EmployeesResponse = {
  total: number;
  employees: Employee[];
};

interface EmployeeParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

const getEmployees = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
}: EmployeeParams) => {
  const response = await axios.get<EmployeesResponse>(
    apiEndpoints.users.employees,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
      },
    },
  );
  return response.data;
};

export const useGetEmployees = (params: EmployeeParams) => {
  return useQuery({
    queryKey: queryKeys.employees.list(params),
    queryFn: () => getEmployees(params),
  });
};

const createEmployee = async (employeeData: EmployeeFormData) => {
  return axios.post(apiEndpoints.users.employees, employeeData);
};

export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeData: EmployeeFormData) =>
      createEmployee(employeeData),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.all }),
  });
};

const deleteEmployee = async (employeeId: string) => {
  return axios.delete(apiEndpoints.users.employee(employeeId));
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeId: string) => deleteEmployee(employeeId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.all }),
  });
};

const updateEmployee = async (
  employeeId: string,
  employeeData: Partial<EmployeeFormData>,
) => axios.put(apiEndpoints.users.employeeUpdate(employeeId), employeeData);

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeId,
      employeeData,
    }: {
      employeeId: string;
      employeeData: Partial<EmployeeFormData>;
    }) => updateEmployee(employeeId, employeeData),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.all }),
  });
};

const deleteMultipleEmployees = (employeeIds: string[]) => {
  return axios.delete(apiEndpoints.users.employeesMultiple, {
    data: { employeeIds },
  });
};

export const useDeleteMultipleEmployees = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeIds: string[]) => deleteMultipleEmployees(employeeIds),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.all }),
  });
};

const getShouldShareAllCircuits = async () => {
  const response = await axios.get<{ shouldShareAllCircuits: boolean }>(
    apiEndpoints.companies.shouldShareAllCircuits,
  );
  return response.data;
};

export const useGetShouldShareAllCircuits = () => {
  return useQuery({
    queryKey: queryKeys.shouldShareAllCircuits,
    queryFn: () => getShouldShareAllCircuits(),
  });
};

const updateShouldShareAllCircuits = async (
  shouldShareAllCircuits: boolean,
) => {
  return axios.put(apiEndpoints.companies.shouldShareAllCircuits, {
    shouldShareAllCircuits,
  });
};

export const useUpdateShouldShareAllCircuits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shouldShareAllCircuits: boolean) =>
      updateShouldShareAllCircuits(shouldShareAllCircuits),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.shouldShareAllCircuits,
      }),
  });
};
